import * as angular from 'angular';
import * as _ from 'lodash';
import template from './shopOperatorDashboard.tpl.html';
import { States } from "../../shared/config/constant.module";
import { StateParams } from "../../common";
import UIUtilityServiceName, { UIUtilityService, EnumOption } from "../../services/utility/uiUtility.service";
import ShopServiceName, { ShopService } from "../../services/shops/shops.service";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import * as Highcharts from 'highcharts/highstock';
import { apsDisplayEnums } from '../../shared/dto/enums';
import { IInterval, Interval } from '../../directives/interval.factory';
import AuthDataServiceName, { AuthDataService } from "../../services/auth/authData.service";
import * as moment from 'moment';
import { apsViewModels } from '../../shared/dto/APS.ViewModels';
import ShopOperatorServiceName, { ShopOperatorService } from '../../services/dashboard/shopOperator.service';

let requires = [
  UIUtilityServiceName,
  ShopServiceName,
  LoaderServiceName,
  AuthDataServiceName,
  ShopOperatorServiceName
];

export interface CavityCorrelation {
  cavityName: string;
  color: string;
  cavityValue: string;
}

export interface AreaPercent {
  lehrInPercentClass: string;
  lehrInPercent: number;
  lehrOutPercentClass: string;
  lehrOutPercent: number;
  packPercentClass: string;
  packPercent: number;
}

export interface LossTypeInfo {
  checkLosses: number;
  checkClass: string;
  dimensionalLosses: number;
  dimensionalClass: string;
  dipPlugLosses: number;
  dipPlugClass: string;
  moldLosses: number;
  moldClass: string;
  thinwareLosses: number;
  thinwareClass: string;
  topBottomLosses: number;
  topBottomClass: string;
  visualLosses: number;
  visualClass: string;
  nonMappedLosses: number;
  nonMappedClass: string;
}

export interface ShopOperatorInfo {
  backCavities: CavityCorrelation[];
  middleCavities: CavityCorrelation[];
  frontCavities: CavityCorrelation[];
  areaPercent: AreaPercent;
  lossTypeInfo: LossTypeInfo;
  bottlesPerMinute: number;
}

export class ShopOperatorDashboardController {
  plantId: string;
  shopId: string;
  shopName: string;
  plantName: string;
  chartOptions: any;
  startDate: Date;
  endDate: Date;
  shopOperatorInfo: ShopOperatorInfo;
  countDisplayIntervalOptions: EnumOption[];
  countsInterval: apsDisplayEnums.CountDisplayInterval;
  $state: angular.ui.IStateService;
  loaderService: LoaderService;
  uiUtilityService: UIUtilityService;
  shopConfig: apsViewModels.ShopConfigurationWithShifts;
  shopService: ShopService;
  authDataService: AuthDataService;
  $interval: angular.IIntervalService;
  interval: IInterval = null;
  $q: angular.IQService;
  shopOperatorService: ShopOperatorService;
  private $scope: angular.IScope;

  constructor(
    $stateParams: StateParams,
    shopService: ShopService,
    loaderService: LoaderService,
    $scope: angular.IScope,
    uiUtilityService: UIUtilityService,
    authDataService: AuthDataService,
    shopOperatorService: ShopOperatorService,
    $state: angular.ui.IStateService,
    $interval: angular.IIntervalService,
    $q: angular.IQService) {

    this.plantId = $stateParams.plantId;
    this.shopId = $stateParams.shopId;
    this.loaderService = loaderService;
    this.shopService = shopService;
    this.shopOperatorService = shopOperatorService;
    this.shopName = null;
    this.plantName = null;
    this.$state = $state;
    this.$scope = $scope;
    this.$interval = $interval;
    this.uiUtilityService = uiUtilityService;
    this.authDataService = authDataService;
    this.$q = $q;

    this.$scope.$on('$destroy', () => {
      this.destroyInterval();
    });

    this.createInterval();
    this.initPage();
  }

  initPage() {
    this.loaderService.setAppLoading(true);
    this.shopService.getShopConfiguration(this.shopId).then(shop => {
      this.shopName = shop.name;
      this.plantName = shop.plantName;
      this.shopConfig = shop;
    })
      .then(() => {
        this.countDisplayIntervalOptions = this.uiUtilityService
          .getListOptionsFromEnum(apsDisplayEnums.CountDisplayInterval, null, null, o => o);
      })
      .then(() => {
        this.countsInterval = this.authDataService.getCountInterval();
        this.mapCountInterval();
      })
      .then(() => {
        this.shopOperatorService
          .getShopOperator(this.shopId, this.startDate, this.endDate)
          .then((result) => {
            this.mapShopOperatorResult(result);
            this.loaderService.setAppLoading(false);
          });
      });
  }

  mapShopOperatorResult(result: apsViewModels.ShopOperatorType) {
    const backCavities = this.getCavityCorrelations(result.cavityCorrelations, 'B');
    const middleCavities = this.getCavityCorrelations(result.cavityCorrelations, 'M');
    const frontCavities = this.getCavityCorrelations(result.cavityCorrelations, 'F');
    const areaPercent = this.getAreaPercent(result.countSummaries);
    const heSummary = this.getHotEndSummary(result.countSummaries);
    const lossTypeInfo = this.getLossTypeInfo(result.lossTypeSummary, heSummary.inspectCount);
    const bottlesPerMinute = result.bottlesPerMinute || 0;

    this.shopOperatorInfo = {
      backCavities: backCavities,
      middleCavities: middleCavities,
      frontCavities: frontCavities,
      areaPercent: areaPercent,
      lossTypeInfo: lossTypeInfo,
      bottlesPerMinute: bottlesPerMinute
    };

    this.mapSwabbingData(result.swabbingTrendSummary);
  }

  getLossTypeValue(lossTypeSummary: apsViewModels.LossTypeSummary, lossTypeName: string, heInspect: number): number {

    // find the detail that matches the loss type
    let item = _.find(lossTypeSummary.details, (x) => {
      if (x.lossTypeName === lossTypeName) {
        return true;
      }
      return false;
    });

    // get the value if it exist
    let value = (item && item.rejectCount && heInspect)
      ? (item.rejectCount / heInspect) * 100
      : 0.0;

    return value;
  }

  getLossTypeInfo(lossTypeSummary: apsViewModels.LossTypeSummary, heInspect: number): LossTypeInfo {

    let checkLosses = this.getLossTypeValue(lossTypeSummary, 'Checks', heInspect);
    let dimensionalLosses = this.getLossTypeValue(lossTypeSummary, 'Dimensional', heInspect);
    let dipPlugLosses = this.getLossTypeValue(lossTypeSummary, 'Dip/Plug Loss', heInspect);
    let moldLosses = this.getLossTypeValue(lossTypeSummary, 'Mold Loss', heInspect);
    let thinwareLosses = this.getLossTypeValue(lossTypeSummary, 'Thinware Loss', heInspect);
    let topBottomLosses = this.getLossTypeValue(lossTypeSummary, 'Top/Bottom Insp', heInspect);
    let visualLosses = this.getLossTypeValue(lossTypeSummary, 'Visual Loss', heInspect);
    let nonMappedLosses = this.getLossTypeValue(lossTypeSummary, 'Not Mapped', heInspect);

    let lossTypeInfo: LossTypeInfo = {
      checkLosses: checkLosses,
      checkClass: this.getLossTypeClass(checkLosses),
      dimensionalLosses: dimensionalLosses,
      dimensionalClass: this.getLossTypeClass(dimensionalLosses),
      dipPlugLosses: dipPlugLosses,
      dipPlugClass: this.getLossTypeClass(dipPlugLosses),
      moldLosses: moldLosses,
      moldClass: this.getLossTypeClass(moldLosses),
      thinwareLosses: thinwareLosses,
      thinwareClass: this.getLossTypeClass(thinwareLosses),
      topBottomLosses: topBottomLosses,
      topBottomClass: this.getLossTypeClass(topBottomLosses),
      visualLosses: visualLosses,
      visualClass: this.getLossTypeClass(visualLosses),
      nonMappedLosses: nonMappedLosses,
      nonMappedClass: this.getLossTypeClass(nonMappedLosses),
    };
    return lossTypeInfo;
  }

  getLossTypeClass(checkLosses: number): string {
    let lossTypeClass = 'bg-success';

    // yellow if between 1 to 2 percent
    if (checkLosses > 1.0) {
      lossTypeClass = 'bg-warning';
    }

    // red if its greater than 2 percent
    if (checkLosses > 2.0) {
      lossTypeClass = 'bg-danger';
    }

    return lossTypeClass;
  }

  getCavityCorrelations(cavitiesFromRequest: apsViewModels.CavityCorrelationCountSummary[], cavityLocation: string) {
    const cavityCorrelation: CavityCorrelation[] = [];
    _.forEach(cavitiesFromRequest, (cavityInfo) => {
      if (cavityInfo.cavity.indexOf(cavityLocation) > -1 && cavityInfo.cavity !== "M+U") {
        const cavityName = cavityInfo.cavity;
        const color = this.getCorrelationColor(cavityInfo.inspectCount, cavityInfo.rejectCount);
        const cavityValue = this.getCavityValue(cavityInfo.inspectCount, cavityInfo.rejectCount);
        const cavityItem: CavityCorrelation = {
          cavityName: cavityName,
          color: color,
          cavityValue: cavityValue
        };
        cavityCorrelation.push(cavityItem);
      }
    });
    return cavityCorrelation;
  }
  
  getCavityValue(inspectCount: number, rejectCount: number): string {
    let value = 0.0;

    if (inspectCount && rejectCount) {
      value = 100 * (rejectCount / inspectCount) || 0.0;
    }

    return value.toFixed(2);
  }

  getCorrelationColor(inspectCount: number, rejectCount: number) {
    let color = '000000';
    const effeciencyPercent = 100 * (rejectCount / inspectCount) || 0.0;
    if (effeciencyPercent || effeciencyPercent === 0.0) {
      color = this.uiUtilityService.getCavityCorrelationColor(effeciencyPercent) + 'A0';
    }
    return color;
  }

  getAreaPercent(countSummaries: apsViewModels.CountSummary[]): AreaPercent {
    const heSummary: apsViewModels.CountSummary = this.getHotEndSummary(countSummaries);
    const lehrSummary: apsViewModels.CountSummary = this.getLehrSummary(countSummaries);
    const lehrInPercent: number = (lehrSummary.inspectCount / heSummary.inspectCount * 100) || 0.0;
    const lehrOutPercent: number = ((lehrSummary.inspectCount - lehrSummary.rejectCount) / heSummary.inspectCount * 100) || 0.0;;
    const packPercent: number = this.getPackPercent(countSummaries);
    return {
      lehrInPercent: lehrInPercent,
      lehrInPercentClass: this.getLehrPercentClass(lehrInPercent),
      lehrOutPercent: lehrOutPercent,
      lehrOutPercentClass: this.getLehrPercentClass(lehrOutPercent),
      packPercent: packPercent,
      packPercentClass: this.getPackPercentClass(packPercent)
    };
  }

  getHotEndSummary(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary {

    // find HE summary
    let heItem = _.findLast(countSummaries, (c) => {
      return c.name === 'HE' || c.name === 'CUTS';
    });

    // add default if nothing is returned
    if (!heItem) {
      heItem = {
        name: 'HE',
        inspectCount: 0.0,
        rejectCount: 0.0,
        rejectPercent: 0.0,
        equipmentCounts: [],
      };
    }

    return heItem;
  }

  getLehrSummary(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary {

    // find lehr summary
    let lehrItem = _.findLast(countSummaries, (c) => {
      return c.name === 'LEHR';
    });

    // add default if nothing is returned
    if (!lehrItem) {
      lehrItem = {
        name: 'LEHR',
        inspectCount: 0.0,
        rejectCount: 0.0,
        rejectPercent: 0.0,
        equipmentCounts: [],
      };
    }

    return lehrItem;
  }

  getPackPercentClass(packPercent: number): string {
    let styleClass = 'bg-success';

    if (packPercent && packPercent < 95) {
      styleClass = 'bg-warning';
    }

    if (packPercent && packPercent < 90) {
      styleClass = 'bg-danger';
    }

    return styleClass;
  }

  getLehrPercentClass(lehrPercent: number): string {
    let styleClass = 'bg-success';

    if (lehrPercent && lehrPercent < 98.5) {
      styleClass = 'bg-warning';
    }

    if (lehrPercent && lehrPercent < 97) {
      styleClass = 'bg-danger';
    }

    return styleClass;
  }

  getPackPercent(countSummaries: apsViewModels.CountSummary[]): number {
    let bottlesIn = 0.0;
    let bottlesOut = 0.0;
    let countLen = countSummaries.length || 0;

    if (countLen === 0) {
      return 0.0;
    }

    let lastItem = countSummaries[countLen - 1];
    bottlesIn = countSummaries[0].inspectCount;
    bottlesOut = (lastItem.rejectCount && lastItem.rejectCount > 0.0)
      ? lastItem.inspectCount - lastItem.rejectCount
      : lastItem.inspectCount;

    return (bottlesOut / bottlesIn) * 100.0;
  }

  getColdEndInspectionLoss(countSummaries: apsViewModels.CountSummary[]): number {
    let inspectionLoss = 0.0;
    const inspectionEquipmentItems = this.getColdEndInspectionItems(countSummaries);

    if (inspectionEquipmentItems && inspectionEquipmentItems.length > 0) {
      let totalInspected = 0.0;
      let totalRejected = 0.0;
      _.forEach(inspectionEquipmentItems, (cs) => {
        totalInspected += cs.inspectCount;
        totalRejected += cs.rejectCount;
      });
      inspectionLoss = (totalRejected / totalInspected) * 100;
    }

    return inspectionLoss;
  }

  getColdEndInspectionItems(countSummaries: apsViewModels.CountSummary[]): apsViewModels.CountSummary[] {
    const coldEndInspectionItems = _.filter(countSummaries, (cs) => {
      return cs.name !== "HE" && cs.name !== "CUTS" && cs.name !== "LEHR"
        && cs.name !== "EOL" && cs.name !== "TOTL" && cs.name !== "LOut"
        && cs.name !== "CP" && cs.name !== "SPRY" && cs.name !== "STKR";
    });
    return coldEndInspectionItems;
  }

  getHotEndLoss(countSummaries: apsViewModels.CountSummary[]): number {
    let heLosses = 0.0;
    const heItem = _.findLast(countSummaries, (c) => {
      return c.name === 'HE' || c.name === 'CUTS';
    });

    if (heItem && heItem.inspectCount && heItem.rejectCount) {
      heLosses = (heItem.rejectCount / heItem.inspectCount) * 100;
    }

    return heLosses;
  }

  mapSwabbingData(swabbingTrendSummary: apsViewModels.SwabbingTrendSummary[]) {
    this.chartOptions = this.getChartOptions(swabbingTrendSummary);
    Highcharts.chart('container', this.chartOptions);
  }

  countsIntervalChanged(): void {
    this.mapCountInterval();
    this.update();
  }

  update() {
    this.loaderService.setAppLoading(true);
    this.shopOperatorService
      .getShopOperator(this.shopId, this.startDate, this.endDate)
      .then((result) => {
        this.mapShopOperatorResult(result);
        this.loaderService.setAppLoading(false);
      });
  }

  createInterval() {
    this.interval = new Interval(() => {
      this.update();
    }, this.$interval, 1000 * 60 * 5);
    this.interval.start();
  }

  destroyInterval() {
    this.interval.destroy();
    this.interval = null;
  }

  mapCountInterval() {
    this.authDataService.setCountInterval(this.countsInterval);
    this.endDate = moment().toDate();
    let now = moment();
    let remainderMinutes = now.minute() % 10;
    let remainderSeconds = now.second();
    let remainderMilliSeconds = now.millisecond();
    switch (this.countsInterval) {
      case apsDisplayEnums.CountDisplayInterval.CurrentHour:
        this.startDate = moment().startOf('hour').toDate();
        break;
      case apsDisplayEnums.CountDisplayInterval.PreviousHour:
        this.endDate = moment().startOf('hour').toDate();
        this.startDate = moment(this.endDate).subtract(1, 'hour').toDate();
        break;
      case apsDisplayEnums.CountDisplayInterval.CurrentShift:
        this.startDate = this.shopConfig.shiftsInLast24Hours[0].startTime;
        break;
      case apsDisplayEnums.CountDisplayInterval.LastShift:
        this.endDate = this.shopConfig.shiftsInLast24Hours[1].stopTime;
        this.startDate = this.shopConfig.shiftsInLast24Hours[1].startTime;
        break;
      case apsDisplayEnums.CountDisplayInterval.ShiftBeforeLast:
        this.endDate = this.shopConfig.shiftsInLast24Hours[2].stopTime;
        this.startDate = this.shopConfig.shiftsInLast24Hours[2].startTime;
        break;
      case apsDisplayEnums.CountDisplayInterval.RollingHour:
				this.endDate = moment(now)
					.subtract(remainderMinutes, 'minute')
					.subtract(remainderSeconds, 'second')
					.subtract(remainderMilliSeconds, 'millisecond')
					.toDate();
				this.startDate = moment(this.endDate)
					.subtract(1, 'hour')
					.toDate();
        break;
      case apsDisplayEnums.CountDisplayInterval.LastTwentyMinutes:
				this.endDate = moment(now)
					.subtract(remainderMinutes, 'minute')
					.subtract(remainderSeconds, 'second')
					.subtract(remainderMilliSeconds, 'millisecond')
					.toDate();
				this.startDate = moment(this.endDate)
					.subtract(20, 'minute')
					.toDate();
        break;
      default: // default to 24 hours
        this.endDate = moment().startOf('hour').toDate();
        this.startDate = moment(this.endDate).subtract(1, 'days').toDate();
        break;
    }
  }

  getSampleData(details: apsViewModels.SwabbingTrendDetails[]) {
    const sampleSeries = [];

    details.forEach(x => {
      let dateTime = new Date(x.isoTimeStamp);
      let datapoint = {
        x: moment(dateTime).toDate().getTime(),
        y: x.count
      };
      sampleSeries.push(datapoint);
    });

    return sampleSeries;
  }

  getSeries(swabbingTrendSummary: apsViewModels.SwabbingTrendSummary[]) {
    const seriesData = [];

    swabbingTrendSummary.forEach(x => {
      const item = {
        name: 'Section ' + x.section,
        data: this.getSampleData(x.details),
        legendIndex: x.section
      };
      seriesData.push(item);
    });

    return seriesData;
  }

  getChartOptions(swabbingTrendSummary: apsViewModels.SwabbingTrendSummary[]) {
    const seriesData = this.getSeries(swabbingTrendSummary);
    return {

      chart: {
        zoomType: 'x'
      },

      time: {
        useUTC: false
      },

      title: {
        text: ''
      },

      yAxis: {
        title: {
          text: 'Number of Gobs Lost'
        }
      },

      xAxis: {
        type: 'datetime',
        title: {
          text: 'Date/Time'
        },
        dateTimeLabelFormats: {
          second: '%l:%M:%S %p',
          minute: '%l:%M %p',
          hour: '%l %p',
          day: '%m/%d/%Y',
          month: '%m/%d/%Y',
          year: '%m/%d/%Y'
        }
      },

      tooltip: {
        xDateFormat: '%m/%d/%Y %l:%M %p'
      },

      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },

      plotOptions: {
        series: {
          marker: {
            enabled: false
          }
        }
      },

      series: seriesData,

      credits: {
        enabled: false
      },

      responsive: {
        rules: [{
          condition: {
            maxWidth: 500
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom'
            }
          }
        }]
      }
    };
  }
}

export default angular.module('aps.controller.shopOperatorDashboard', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.SHOP_OPERATOR_DASHBOARD,
      url: '/shopOperatorDashboard',
      controller: 'ShopOperatorDashboardCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Shop Operator Dashboard'
      }
    });
  })
  .controller('ShopOperatorDashboardCtrl', ShopOperatorDashboardController)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/dashboard/shopOperatorDashboard.controller.ts